import { useCallback, useEffect, useMemo, useState } from 'react';

import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { createColumnHelper } from '@tanstack/react-table';

import { useAppDispatch } from 'src/app/store';
import { DataGrid } from 'src/common/DataGrid';
import {
  PassengerDuplicateRead,
  usePassengersDuplicatesRetrieveQuery,
  usePassengersMergeCreateMutation,
} from 'src/common/external/bambi-api/bambiApi';
import { Button } from 'src/common/primitives/Button';
import { LoadingIndicator } from 'src/common/primitives/LoadingIndicator';
import { Modal } from 'src/common/primitives/Modal';
import { show } from 'src/common/primitives/Toast/toast.slice';
import { removeUnitedStatesFromAddress } from 'src/common/util/removeUnitedStatesFromAddress';
import formatServerError from 'src/common/util/serverErrorFormatter';

import { PassengerEmptyState } from '../PassengerEmptyState';
import { IsSameCell } from './IsSameCell';
import { DuplicatePassenger } from './types';

interface PassengerMergeModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export function PassengerMergeModal({
  open,
  setOpen,
}: PassengerMergeModalProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});

  const { isLoading, data: duplicatePassengerList } =
    usePassengersDuplicatesRetrieveQuery({}, { skip: !open });
  const dispatch = useAppDispatch();

  const [executeMerge] = usePassengersMergeCreateMutation({});

  const flatList: DuplicatePassenger[] = useMemo(() => {
    const list: DuplicatePassenger[] = [];
    (duplicatePassengerList as unknown as PassengerDuplicateRead[])?.forEach(
      (p) => {
        const parentId = p.base_passenger.id;

        list.push(p.base_passenger);
        p.possible_duplicates.forEach((d) => {
          list.push({
            ...d,
            parentId,
          });
        });
      }
    );
    return list;
  }, [duplicatePassengerList]);

  const selectedPassengersList = useMemo(() => {
    return Object.keys(rowSelection).reduce(
      (acc: DuplicatePassenger[], key: keyof typeof rowSelection) => {
        if (!rowSelection[key]) {
          return acc;
        }

        acc.push(flatList[parseInt(key)]);
        return acc;
      },
      [] as DuplicatePassenger[]
    );
  }, [flatList, rowSelection]);

  const parentPassenger = useMemo(() => {
    if (!selectedPassengersList || !selectedPassengersList.length) {
      return null;
    }

    return (
      flatList.find(
        (record) => record.id === selectedPassengersList[0].parentId
      ) ?? null
    );
  }, [selectedPassengersList, flatList]);

  const handleMerge = useCallback(async () => {
    if (!parentPassenger) {
      dispatch(
        show({
          title: 'Something went wrong, unable to find base passenger',
          type: 'error',
        })
      );

      return;
    }

    try {
      const result = await executeMerge({
        passengerMergeRequest: {
          passenger_ids: [
            parentPassenger.id,
            ...selectedPassengersList.map((p) => p.id),
          ],
        },
      }).unwrap();

      if (result?.merge_failure?.length) {
        dispatch(
          show({
            type: 'error',
            title: `Failed to merge (${result.merge_failure?.length}) passengers`,
            description: result.errors?.map((error) => error.error).join('\n'),
          })
        );
        return;
      }

      dispatch(
        show({
          title: `Successfully (${selectedPassengersList.length}) merged passengers`,
          type: 'success',
        })
      );
      setOpen(false);
    } catch (e) {
      dispatch(
        show({
          title: 'Failed to merge passengers',
          type: 'error',
          description: formatServerError(e),
        })
      );
    }
  }, [
    dispatch,
    executeMerge,
    parentPassenger,
    selectedPassengersList,
    setOpen,
  ]);

  useEffect(() => {
    if (!open) {
      setCurrentStep(0);
      setRowSelection({});
    }
  }, [open]);

  return (
    <Modal open={open} setOpen={setOpen}>
      {isLoading ? (
        <LoadingIndicator />
      ) : (
        <div className="flex flex-col gap-2">
          {currentStep === 0 ? (
            <SelectDuplicatesScreen
              duplicatePassengerList={flatList}
              rowSelection={rowSelection}
              setRowSelection={setRowSelection}
            />
          ) : (
            <ConfirmMergeScreen
              passengersToMerge={selectedPassengersList}
              parentPassenger={parentPassenger}
            />
          )}

          <div className="flex flex-row justify-evenly gap-2">
            <Button
              variant="secondary-outline"
              className="w-full"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="w-full"
              disabled={
                currentStep === 0 && selectedPassengersList?.length === 0
              }
              onClick={() => {
                if (currentStep === 0) {
                  setCurrentStep(1);
                } else {
                  handleMerge();
                }
              }}
            >
              {currentStep === 0 ? 'Verify' : 'Merge'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function SelectDuplicatesScreen({
  duplicatePassengerList,
  rowSelection,
  setRowSelection,
}: {
  duplicatePassengerList: DuplicatePassenger[];
  rowSelection: Record<string, boolean>;
  setRowSelection: React.Dispatch<React.SetStateAction<{}>>;
}) {
  const columnHelper = createColumnHelper<DuplicatePassenger>();

  const selectedParentId = useMemo(() => {
    const selected = Object.keys(rowSelection).filter((n) => !!rowSelection[n]);

    if (!selected.length) {
      return null;
    }

    return duplicatePassengerList[parseInt(selected[0])]?.parentId;
  }, [duplicatePassengerList, rowSelection]);

  const shouldDisableRow = useCallback(
    (row: DuplicatePassenger) => {
      if (!selectedParentId) {
        return false;
      }

      return row.parentId !== selectedParentId;
    },
    [selectedParentId]
  );

  const mergePassengerColumns = [
    columnHelper.display({
      id: 'selector',
      cell({ row }) {
        const isPossibleDuplicate = row.original.parentId;
        if (!isPossibleDuplicate) {
          return <div>Base</div>;
        }

        const disabled = shouldDisableRow(row.original);

        return (
          <input
            type="checkbox"
            className={disabled ? 'border-slate-100 bg-slate-100' : ''}
            checked={row.getIsSelected()}
            disabled={disabled}
            onChange={row.getToggleSelectedHandler()}
            data-testid={`select-duplicate-control-${row.original.id}`}
          />
        );
      },
    }),
    columnHelper.accessor('full_name', {
      cell: IsSameCell,
      header: 'Name',
    }),
    columnHelper.display({
      id: 'address',
      header: 'Address',
      cell(props) {
        const { row } = props;
        return (
          <div key={row.original.id}>
            {row.original.passenger_addresses.map((a, i) => {
              const label = a.address.location?.label ?? '';

              return (
                <div key={`${row.original.id}-${a.id}`}>
                  <IsSameCell
                    {...props}
                    comparator={(parentRow) => {
                      return !!parentRow.passenger_addresses.find(
                        (parentAddress) =>
                          parentAddress.address.location?.place_id ===
                          a.address.location?.place_id
                      );
                    }}
                  >
                    {removeUnitedStatesFromAddress(label)}
                  </IsSameCell>
                </div>
              );
            })}
          </div>
        );
      },
    }),
    columnHelper.accessor('email', {
      cell: IsSameCell,
      header: 'Email',
    }),
    columnHelper.accessor('phone_number', {
      cell: IsSameCell,
      header: 'Phone Number',
    }),
    columnHelper.accessor('weight', {
      cell: IsSameCell,
      header: 'Weight',
    }),
    columnHelper.accessor('dob', {
      cell: IsSameCell,
      header: 'Date of Birth',
    }),
    columnHelper.accessor('gender', {
      cell: IsSameCell,
      header: 'Gender',
    }),
  ];

  return (
    <DataGrid
      tableId="passenger-merge"
      columns={mergePassengerColumns}
      data={duplicatePassengerList || []}
      totalCount={duplicatePassengerList?.length || 0}
      emptyState={<PassengerEmptyState />}
      rowSelection={rowSelection}
      setRowSelection={setRowSelection}
      includePagination={false}
      pagination={{
        pageIndex: 0,
        pageSize: Infinity,
      }}
    />
  );
}

function ConfirmMergeScreen({
  passengersToMerge,
  parentPassenger,
}: {
  passengersToMerge: DuplicatePassenger[];
  parentPassenger: DuplicatePassenger | null;
}) {
  if (!parentPassenger) {
    return (
      <div>
        <div className="text-center text-lg">Unexpected state</div>
        <div>
          The parent passenger was not found, please return to the previous
          step.
        </div>
      </div>
    );
  }

  if (!passengersToMerge.length) {
    return (
      <div>
        <div className="text-center text-lg">Unexpected state</div>
        <div>
          No passengers to merge found, please return to the previous step.
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row items-center justify-center gap-2 text-lg">
        <ExclamationTriangleIcon color="red" className="w-4" />
        Merge Passengers
        <ExclamationTriangleIcon color="red" className="w-4" />
      </div>
      <div data-testid="merge-confirmation-message">
        Merge <strong>{passengersToMerge.length}</strong> record(s) into{' '}
        <strong>{parentPassenger.full_name}</strong> record?
      </div>
      <div>
        <strong>This action is not reversable.</strong>
      </div>
    </div>
  );
}
